import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Platform } from "types/platform";

interface PlatformGroup {
  id: string | number;
  name: string;
  platforms: Platform[];
}

interface UGCPlatformsProps {
  platforms: Platform[];
  platformGroupsCreating: boolean;
  platformGroups: PlatformGroup[];
  setUgcData: (ugcData: string[]) => void;
  ugcSelectedValues: (string | number)[];
  ugcPlatformGroups: PlatformGroup[];
  ugcPlatforms: Platform[]; // ← սա edit-ի համար ընտրվածները
}

export const UGCPlatforms: React.FC<UGCPlatformsProps> = ({
  platformGroups,
  setUgcData,
  ugcSelectedValues,
  ugcPlatformGroups,
  ugcPlatforms
}) => {
  const [platforms, setPlatforms] = useState<Platform[]>(platformGroups[0].platforms);
  const [hovered, setHovered] = useState<string | null>(null);

  const allSelected = platforms.every((p) => p.check);
  const someSelected = platforms.some((p) => p.check);

  const YT_ID = platforms?.find((val) => val.name === "YouTube Content ID")?.id ?? "";

  const handleSelectAll = (checked: boolean) => {
    setPlatforms((prev) => prev.map((p) => ({ ...p, check: checked })));
  };

  const toggleValue = (id: string) => {
    setPlatforms((prev) => {
      if (id === YT_ID) {
        const newYTCheck = !prev.find((p) => p.id === YT_ID)?.check;
        return prev.map((p) => ({
          ...p,
          check: p.id === YT_ID ? newYTCheck : newYTCheck ? p.check : false,
        }));
      }
      const youtubeSelected = prev.find((p) => p.id === YT_ID)?.check;
      if (!youtubeSelected) return prev;

      return prev.map((p) => (p.id === id ? { ...p, check: !p.check } : p));
    });
  };


  useEffect(() => {
    if (ugcPlatforms?.length > 0) {
      setPlatforms((prev) =>
        prev.map((p) => ({
          ...p,
          check: ugcPlatforms.some((u) => String(u.id) === String(p.id)),
        }))
      );
    }
  }, [ugcPlatforms]);

  useEffect(() => {
    const checkedIds = platforms
      .filter((item) => item.check)
      .map((item) => String(item.id));

    setUgcData(checkedIds);
  }, [platforms, setUgcData]);

  return (
    <Container id="ugc-platforms">
      {!ugcSelectedValues.length && (
        <SelectAllWrapper>
          <OptionItem onClick={() => handleSelectAll(!allSelected)} selected={allSelected}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = !allSelected && someSelected;
                }}
                readOnly
              />
              <OptionLabel>Select all</OptionLabel>
            </div>
          </OptionItem>
        </SelectAllWrapper>
      )}

      <Grid>
        {[...platforms]
          .sort((a, b) => {
            if (a.id === YT_ID) return -1;
            if (b.id === YT_ID) return 1;
            return a.name.localeCompare(b.name);
          })
          .map((platform) => {
            const youtubeSelected = platforms.find((p) => p.id === YT_ID)?.check;
            const disabled = platform.id !== YT_ID && !youtubeSelected;

            return (
              <OptionItem
                key={platform.id}
                onClick={() => !disabled && toggleValue(platform.id)}
                selected={!!platform.check}
                onMouseEnter={() => disabled && setHovered(platform.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
                  <Checkbox type="checkbox" checked={!!platform.check} readOnly disabled={disabled} />
                  <OptionLabel>{platform.name}</OptionLabel>
                  {hovered === platform.id && (
                    <Tooltip>YouTube Content ID must be selected first</Tooltip>
                  )}
                </div>
              </OptionItem>
            );
          })}
      </Grid>

      {allSelected && (
        <Box>
          <Text>
            You can add platforms with a digital fingerprint system (UGC platforms) only for
            materials for which you hold exclusive rights. Please review the usage rules in the
            prompt carefully! Improper use of Content ID and Fingerprinting tools for non-exclusive
            content may result in account suspension.
          </Text>

          <Text>Monetizing the following recordings is strictly prohibited:</Text>

          <div>
            <Text>Non-exclusive recordings:</Text>
            <span>
              This includes cover versions, unofficial remixes, karaoke, sound-alike recordings,
              compilations, live recordings, and "best-of" releases from various artists.
            </span>
          </div>

          <div>
            <Text>Royalty-free content: </Text>
            <span>
              Recordings and beats from royalty-free libraries, as well as those used in games,
              movies, and other works, are prohibited for monetization.
            </span>
          </div>

          <div>
            <Text>Generic content:</Text>
            <span>
              Content lacking sufficient originality, such as nature sounds, background sounds,
              ASMR, white noise, sound effects, background melodies, and meditative content.
            </span>
          </div>

          <div>
            <Text>Public domain content: </Text>
            <span>
              Recordings based on compositions in the public domain due to expired copyrights.
            </span>
          </div>

          <div>
            <Text>Non-musical recordings: </Text>
            <span>
              Audiobooks, podcasts, speeches, movies, prayers, gameplay videos are not allowed.
            </span>
          </div>

          <div>
            <Text>More than one work in a track: </Text>
            <span>
              Only one work (one song) per track is allowed. DJ mixes and mashups are not allowed.
            </span>
          </div>

          <div>
            <Text>Tracks over 10 minutes long:</Text>
            <span>Recordings exceeding 10 minutes.</span>
          </div>

          <div>
            <Text>These rules ensure proper content usage and prevent violations.</Text>
          </div>
        </Box>
      )}
    </Container>
  );
};



const Container = styled.div`
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #00000033;
`;

const SelectAllWrapper = styled.div`
  margin-bottom: 27px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 42px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 12px;
    margin-bottom: 24px;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const OptionItem = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  position: relative;
`;

const OptionLabel = styled.span`
  margin-left: 6px;
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #6777FF;
  appearance: none;
  cursor: pointer;
  position: relative;
  background-color: ${(props) => (props.checked ? "#6777FF" : "#fff")};

  &:checked::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 6px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: -28px;
  left: 0;
  background: #333;
  color: white;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 5px;
  white-space: nowrap;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  height: 530px;
  border-radius: 15px;
  background: #EFFFF1;
  font-family: SF Pro Display;
  font-size: 15px;
  line-height: 20px;
  padding: 22px 60px 31px 25px;
  margin-bottom: 35px;
`;

const Text = styled.span`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  color: #000000;
`;
